'use client';

import { useEffect, useRef, useState } from 'react';
import { Button } from '@/shared/ui/shadcn/button';
import { Loader2, Plus, X, AlertTriangle } from 'lucide-react';
import { imageUploadService } from '@/shared/lib/services/image-upload.service';
import { validateFile } from '@/shared/lib/file-validation';
import { toast } from '@/shared/lib/toast';
import { supportAttachmentService } from '../api/support-attachment.service';
import { MAX_ATTACHMENTS } from '../types';
import { useTranslations } from 'next-intl';

// Source-type gate for support attachments — validated at the component
// layer (message selection happens here, not in the upload service).
const SUPPORT_ATTACHMENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export type AttachmentPhase = 'uploading' | 'ready' | 'failed';

export interface AttachmentItem {
  localId: string;
  previewUrl: string;
  fileName: string;
  phase: AttachmentPhase;
  progress: number;
  remoteId?: string;
}

interface AttachmentPickerProps {
  attachments: AttachmentItem[];
  onChange: (next: AttachmentItem[]) => void;
  disabled?: boolean;
}

/**
 * Attachment picker for the Contact Support form.
 *
 * Uploads start immediately when the user picks files. Each row tracks its own
 * upload phase; the parent only passes a ticket `attachment_ids` payload once
 * all rows are `ready`. Failed rows stay in the list with a retry affordance,
 * but are excluded from submit.
 */
export function AttachmentPicker({
  attachments,
  onChange,
  disabled = false,
}: AttachmentPickerProps) {
  const t = useTranslations('feedback.attachments');
  const tUpload = useTranslations('fileUpload');
  const inputRef = useRef<HTMLInputElement>(null);
  const [uid, setUid] = useState(0);

  // Revoke blob URLs on unmount. We stash the latest list in a ref so the
  // cleanup reads the current rows (including any added after mount) without
  // having to re-register the effect and tear down the revoker each render.
  const revokeRef = useRef(attachments);
  revokeRef.current = attachments;
  useEffect(() => {
    return () => {
      revokeRef.current.forEach((a) => URL.revokeObjectURL(a.previewUrl));
    };
  }, []);

  const remainingSlots = MAX_ATTACHMENTS - attachments.length;

  const handlePick = () => {
    inputRef.current?.click();
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    // Reject unsupported/over-sized sources before seeding optimistic rows.
    const validFiles: File[] = [];
    for (const file of Array.from(files)) {
      const result = validateFile(file, SUPPORT_ATTACHMENT_TYPES);
      if (result.valid) {
        validFiles.push(file);
      } else if (result.error) {
        toast.error(tUpload(result.error.code, result.error.params));
      }
    }
    if (validFiles.length === 0) return;

    const toUpload = validFiles.slice(0, remainingSlots);

    // Seed optimistic rows in uploading state immediately.
    const seeded: AttachmentItem[] = toUpload.map((file, idx) => ({
      localId: `att-${uid + idx}`,
      previewUrl: URL.createObjectURL(file),
      fileName: file.name,
      phase: 'uploading',
      progress: 0,
    }));
    setUid((u) => u + toUpload.length);

    const next = [...attachments, ...seeded];
    onChange(next);

    // Kick off per-file uploads in parallel. We patch the list by localId
    // as each settles to avoid stomping on the user's removal clicks.
    seeded.forEach((row, i) => {
      const file = toUpload[i];
      if (!file) return;
      uploadOne(row.localId, file, (patch) => {
        patchRow(row.localId, patch);
      });
    });
  };

  // Closure over the latest `attachments` via a ref-like pattern: we read
  // from the parent's `attachments` each time onChange is derived via the
  // functional update below. Since `attachments` is a prop, compute from the
  // freshest snapshot by routing all mutations through onChange.
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;

  const patchRow = (localId: string, patch: Partial<AttachmentItem>) => {
    const current = attachmentsRef.current;
    onChange(
      current.map((a) => (a.localId === localId ? { ...a, ...patch } : a))
    );
  };

  async function uploadOne(
    localId: string,
    file: File,
    patch: (p: Partial<AttachmentItem>) => void
  ) {
    try {
      const result = await imageUploadService.uploadSingle<void>(
        file,
        {
          type: 'angle',
          onProgress: (p) => patch({ progress: p.percentage }),
          onCleanupFailure: async (id) => {
            try {
              await supportAttachmentService.remove(id);
            } catch {
              // Orphan will be reaped by support-attachments:cleanup-orphans.
            }
          },
        },
        async (processed) => {
          const res = await supportAttachmentService.create({
            md5: processed.md5,
            fileName: file.name,
            contentType: processed.file.type,
          });
          const uploadPolicy = res.data.attributes.upload_policy;
          if (!uploadPolicy) {
            // upload_policy is null only when the row's file was soft-deleted,
            // which never happens on the create path — fail loudly instead
            // of silently skipping the upload.
            throw new Error(
              'Support attachment created without upload policy'
            );
          }
          return { id: res.data.id, uploadPolicy };
        },
        async (id) => {
          await supportAttachmentService.confirm(id);
        }
      );

      patch({ phase: 'ready', progress: 100, remoteId: result.id });
    } catch {
      patch({ phase: 'failed' });
    }
  }

  const handleRemove = (localId: string) => {
    const target = attachments.find((a) => a.localId === localId);
    if (target?.remoteId) {
      supportAttachmentService.remove(target.remoteId).catch(() => {
        // Orphan swept by daily cleanup.
      });
    }
    if (target) URL.revokeObjectURL(target.previewUrl);
    onChange(attachments.filter((a) => a.localId !== localId));
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-form-label">{t('label')}</span>
        <span className="text-card-meta text-muted-foreground">{t('hint')}</span>
      </div>

      <div className="flex flex-wrap gap-3">
        {attachments.map((a) => (
          <div
            key={a.localId}
            className="relative h-20 w-20 overflow-hidden rounded-lg border bg-muted"
          >
            <img
              src={a.previewUrl}
              alt={a.fileName}
              className="h-full w-full object-cover"
            />
            {a.phase === 'uploading' && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                <Loader2 className="h-6 w-6 animate-spin text-white" />
              </div>
            )}
            {a.phase === 'failed' && (
              <div className="absolute inset-0 flex items-center justify-center bg-red-500/40">
                <AlertTriangle className="h-6 w-6 text-white" />
              </div>
            )}
            {a.phase !== 'uploading' && (
              <button
                type="button"
                onClick={() => handleRemove(a.localId)}
                className="absolute end-1 top-1 rounded-full bg-black/60 p-0.5 text-white hover:bg-black/80"
                aria-label={t('remove')}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}

        {remainingSlots > 0 && !disabled && (
          <Button
            type="button"
            variant="outline"
            onClick={handlePick}
            className="flex h-20 w-20 flex-col items-center justify-center border-dashed"
          >
            <Plus className="h-5 w-5" />
            <span className="mt-1 text-card-meta">{t('add')}</span>
          </Button>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            const files = e.target.files;
            void handleFiles(files);
            // Reset so the same file can be re-picked after removal.
            e.target.value = '';
          }}
        />
      </div>
    </div>
  );
}
